import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Newspaper, 
  Clock, 
  ExternalLink, 
  RefreshCw, 
  AlertTriangle,
  MapPin,
  Eye,
  Calendar,
  Globe,
  Filter,
  Search,
  Users,
  TrendingUp,
  Navigation
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { GeoThreat } from '../dashboard/types/disaster';
import { NewsDataIODisasterExtractor, NewsApiOptions } from '../dashboard/services/newsApiExtractor';

interface NewsArticle {
  article_id: string;
  title: string;
  description: string;
  content: string;
  pubDate: string;
  source_name: string;
  link: string;
  image_url?: string;
  creator?: string[];
  country?: string[];
  category?: string[];
  keywords?: string[];
}

interface LocationData {
  country: string;
  countryCode: string;
  region: string;
  city: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface DisasterNewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDisaster?: GeoThreat | null;
  userLocation?: LocationData | null;
}

const DisasterNewsModal: React.FC<DisasterNewsModalProps> = ({ 
  isOpen, 
  onClose, 
  initialDisaster,
  userLocation 
}) => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [selectedItem, setSelectedItem] = useState<NewsArticle | GeoThreat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const API_KEY = import.meta.env.VITE_NEWSDATA_API_KEY;

  const categories = [
    { value: 'all', label: 'All Disasters' },
    { value: 'natural', label: 'Natural Disasters' },
    { value: 'weather', label: 'Weather Events' },
    { value: 'emergency', label: 'Emergency Response' },
    { value: 'local', label: 'Local News' }
  ];

  // Type guard to check if item is GeoThreat
  const isGeoThreat = (item: NewsArticle | GeoThreat | null): item is GeoThreat => {
    return item !== null && 'coordinates' in item && 'impactedCount' in item;
  };

  // Fetch location-based disaster news
  const fetchLocationBasedNews = async () => {
    if (!API_KEY && import.meta.env?.VITE_USE_MOCK_DATA !== 'true') {
      setError('API key not configured');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const extractor = new NewsDataIODisasterExtractor(API_KEY);
      
      // Prepare location options
      const options: NewsApiOptions = {
        maxResults: 20
      };

      // Add location filtering if user location is available
      if (userLocation) {
        options.countryCode = userLocation.countryCode;
        options.region = userLocation.region;
        options.city = userLocation.city;
      }

      // Fetch articles using the extractor
      const fetchedArticles = await extractor.fetchDisasterNews(options);
      
      // FIXED: Directly use the fetched articles without incorrect mapping
      // The extractor should return NewsArticle[] format, not GeoThreat[]
      setArticles(fetchedArticles);
      setLastUpdated(new Date());
    } catch (error: any) {
      console.error('Error fetching location-based news:', error);
      setError(error.message || 'Failed to fetch news');
      toast.error('Failed to load local disaster news');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal opening and initial data
  useEffect(() => {
    if (isOpen) {
      if (initialDisaster) {
        // If opened with a specific disaster, show it immediately
        setSelectedItem(initialDisaster);
        setAutoRefresh(false);
      } else {
        // Fetch location-based news
        fetchLocationBasedNews();
        setAutoRefresh(true);
      }
    }
  }, [isOpen, initialDisaster, userLocation]);

  // Auto-refresh functionality (only for location-based news)
  useEffect(() => {
    if (!autoRefresh || !isOpen || initialDisaster) return;

    const interval = setInterval(() => {
      fetchLocationBasedNews();
    }, 5 * 60 * 1000); // Refresh every 5 minutes

    return () => clearInterval(interval);
  }, [autoRefresh, isOpen, initialDisaster, userLocation]);

  // Filter articles based on search and category
  const filteredArticles = articles.filter(article => {
    const matchesSearch = searchTerm === '' || 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || 
      (selectedCategory === 'natural' && 
        ['earthquake', 'hurricane', 'tornado', 'flood', 'wildfire', 'tsunami', 'volcano'].some(keyword => 
          article.title.toLowerCase().includes(keyword) || article.description.toLowerCase().includes(keyword)
        )) ||
      (selectedCategory === 'weather' && 
        ['storm', 'hurricane', 'tornado', 'cyclone', 'typhoon', 'weather'].some(keyword => 
          article.title.toLowerCase().includes(keyword) || article.description.toLowerCase().includes(keyword)
        )) ||
      (selectedCategory === 'emergency' && 
        ['emergency', 'rescue', 'evacuation', 'crisis'].some(keyword => 
          article.title.toLowerCase().includes(keyword) || article.description.toLowerCase().includes(keyword)
        )) ||
      (selectedCategory === 'local' && 
        article.country && userLocation && article.country.includes(userLocation.country));

    return matchesSearch && matchesCategory;
  });

  // Combine initial disaster with articles for display
  const displayItems = initialDisaster 
    ? [initialDisaster, ...filteredArticles]
    : filteredArticles;

  const handleItemClick = (item: NewsArticle | GeoThreat) => {
    setSelectedItem(item);
  };

  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getSeverityIndicator = (item: NewsArticle | GeoThreat) => {
    if (isGeoThreat(item)) {
      switch (item.severity) {
        case 'critical': return { level: 'critical', color: 'bg-accent-red', label: 'Critical' };
        case 'high': return { level: 'high', color: 'bg-accent-orange', label: 'High' };
        case 'medium': return { level: 'medium', color: 'bg-accent-yellow', label: 'Medium' };
        case 'low': return { level: 'low', color: 'bg-accent-green', label: 'Low' };
        default: return { level: 'medium', color: 'bg-accent-yellow', label: 'Medium' };
      }
    } else {
      const text = `${item.title} ${item.description}`.toLowerCase();
      const criticalKeywords = ['emergency', 'evacuation', 'death', 'casualties', 'critical', 'severe'];
      const highKeywords = ['warning', 'alert', 'major', 'significant', 'widespread'];
      
      if (criticalKeywords.some(keyword => text.includes(keyword))) {
        return { level: 'critical', color: 'bg-accent-red', label: 'Critical' };
      } else if (highKeywords.some(keyword => text.includes(keyword))) {
        return { level: 'high', color: 'bg-accent-orange', label: 'High' };
      }
      return { level: 'medium', color: 'bg-accent-yellow', label: 'Medium' };
    }
  };

  const getCategoryEmoji = (category: string) => {
    const emojiMap: Record<string, string> = {
      natural_disaster: '🌍',
      human_health: '❤️',
      war_conflict: '⚔️',
      animal_health: '🐾',
      plant_agriculture: '🌱',
      environmental: '🌍',
      technological: '💻'
    };
    return emojiMap[category] || '📰';
  };

  const getLocationText = () => {
    if (!userLocation) return 'Global News';
    
    if (userLocation.city) return `${userLocation.city}, ${userLocation.country}`;
    if (userLocation.region) return `${userLocation.region}, ${userLocation.country}`;
    return userLocation.country;
  };

  const getLocationFlag = () => {
    if (!userLocation?.countryCode) return '🌍';
    
    // Convert country code to flag emoji
    const countryCodeToFlag = (countryCode: string) => {
      return countryCode
        .toUpperCase()
        .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)));
    };
    
    return countryCodeToFlag(userLocation.countryCode);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-accent-red to-accent-orange rounded-lg flex items-center justify-center">
                  <Newspaper className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {initialDisaster ? 'Disaster Details' : 'Local Disaster News'}
                  </h2>
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <span className="text-lg">{getLocationFlag()}</span>
                    <span>{getLocationText()}</span>
                    {userLocation && (
                      <>
                        <span>•</span>
                        <span>Personalized for your area</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!initialDisaster && (
                  <>
                    <button
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      className={`p-2 rounded-lg transition-colors ${
                        autoRefresh ? 'bg-accent-green/20 text-accent-green' : 'bg-dark-700 text-gray-400'
                      }`}
                      title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}
                    >
                      <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                    </button>
                    
                  </>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Status Bar */}
            <div className="px-6 py-3 bg-dark-700/50 border-b border-dark-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-accent-green rounded-full animate-pulse"></div>
                    <span className="text-sm text-gray-300">
                      {initialDisaster ? 'Dashboard Feed' : 'Location-Based Updates'}
                    </span>
                  </div>
                  {lastUpdated && !initialDisaster && (
                    <div className="flex items-center space-x-2 text-sm text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>Updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}</span>
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-400">
                  {displayItems.length} {initialDisaster ? 'item' : 'articles'} found
                </div>
              </div>
            </div>

            {/* Search and Filter - Only show for location-based news */}
            {!initialDisaster && (
              <div className="p-6 border-b border-dark-700">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search local disaster news..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div className="relative">
                    <Filter className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="pl-10 pr-8 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors appearance-none cursor-pointer min-w-[200px]"
                    >
                      {categories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="flex h-[calc(90vh-280px)]">
              {/* Items List */}
              <div className="w-1/2 border-r border-dark-700 overflow-y-auto">
                {error && !initialDisaster ? (
                  <div className="p-6 text-center">
                    <AlertTriangle className="w-12 h-12 text-accent-red mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Error Loading News</h3>
                    <p className="text-gray-400 mb-4">{error}</p>
                    <button
                      onClick={fetchLocationBasedNews}
                      className="px-4 py-2 bg-accent-red/20 text-accent-red rounded-lg hover:bg-accent-red/30 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                ) : isLoading && !initialDisaster ? (
                  <div className="p-6 text-center">
                    <div className="w-8 h-8 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading local disaster news...</p>
                  </div>
                ) : displayItems.length === 0 ? (
                  <div className="p-6 text-center">
                    <Newspaper className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">No Local News Found</h3>
                    <p className="text-gray-400">
                      {userLocation 
                        ? `No disaster news found for ${getLocationText()}. This is good news!`
                        : 'Enable location services for personalized local news'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="p-4 space-y-4">
                    {displayItems.map((item, index) => {
                      const severity = getSeverityIndicator(item);
                      const isSelected = selectedItem === item;
                      const isDisaster = isGeoThreat(item);
                      
                      return (
                        <motion.div
                          key={isDisaster ? item.id : (item as NewsArticle).article_id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => handleItemClick(item)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-primary-400/50 ${
                            isSelected
                              ? 'border-primary-400 bg-primary-400/10'
                              : 'border-dark-600 bg-dark-700/50 hover:bg-dark-700'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              {isDisaster && (
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-lg">{getCategoryEmoji(item.category)}</span>
                                  <span className="text-xs text-primary-400 font-medium">DASHBOARD FEED</span>
                                </div>
                              )}
                              <h3 className="font-semibold text-white mb-1 line-clamp-2">
                                {isDisaster ? `${item.type.charAt(0).toUpperCase() + item.type.slice(1)} in ${item.location}` : item.title}
                              </h3>
                              <p className="text-sm text-gray-400 line-clamp-2">
                                {item.description}
                              </p>
                            </div>
                            <div className={`px-2 py-1 rounded text-xs font-medium text-white ml-2 ${severity.color}`}>
                              {severity.label}
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <div className="flex items-center space-x-2">
                              {isDisaster ? (
                                <>
                                  <span className="capitalize">{item.category.replace('_', ' ')}</span>
                                  <span>•</span>
                                  <div className="flex items-center space-x-1">
                                    <Users className="w-3 h-3" />
                                    <span>{item.impactedCount.toLocaleString()} affected</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <span>{item.source_name}</span>
                                  {item.country && item.country.length > 0 && (
                                    <>
                                      <span>•</span>
                                      <div className="flex items-center space-x-1">
                                        <Globe className="w-3 h-3" />
                                        <span>{item.country[0]}</span>
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                            <span>
                              {formatDistanceToNow(
                                isDisaster ? item.timestamp : new Date(item.pubDate), 
                                { addSuffix: true }
                              )}
                            </span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Item Detail */}
              <div className="w-1/2 overflow-y-auto">
                {selectedItem ? (
                  <div className="p-6">
                    {isGeoThreat(selectedItem) ? (
                      // Render GeoThreat details
                      <>
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span className="text-lg">{getCategoryEmoji(selectedItem.category)}</span>
                              <span className="text-sm text-gray-400 capitalize">{selectedItem.category.replace('_', ' ')}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-400">
                                {selectedItem.timestamp.toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <h1 className="text-2xl font-bold text-white mb-3 capitalize">
                            {selectedItem.type} Emergency in {selectedItem.location}
                          </h1>
                          <div className="flex items-center space-x-4 text-sm text-gray-400 mb-3">
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-4 h-4" />
                              <span>{selectedItem.location}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Users className="w-4 h-4" />
                              <span>{selectedItem.impactedCount.toLocaleString()} people affected</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <TrendingUp className="w-4 h-4" />
                              <span className="capitalize">{selectedItem.severity} severity</span>
                            </div>
                          </div>
                        </div>

                        <div className="mb-6 bg-dark-700/30 rounded-lg p-4">
                          <div className="w-full h-48 bg-gradient-to-br from-dark-600 to-dark-700 rounded-lg flex items-center justify-center mb-4">
                            <div className="text-center text-gray-400">
                              <AlertTriangle className="w-12 h-12 mx-auto mb-2" />
                              <p className="text-sm">Live disaster monitoring data</p>
                            </div>
                          </div>
                        </div>

                        <div className="prose prose-invert max-w-none">
                          <p className="text-lg text-gray-300 mb-4 font-medium">
                            {selectedItem.description}
                          </p>
                          
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-dark-700/50 rounded-lg p-4">
                              <h4 className="text-sm font-semibold text-gray-400 mb-2">Status</h4>
                              <p className="text-white capitalize">{selectedItem.status}</p>
                            </div>
                            <div className="bg-dark-700/50 rounded-lg p-4">
                              <h4 className="text-sm font-semibold text-gray-400 mb-2">Category</h4>
                              <p className="text-white capitalize">{selectedItem.category.replace('_', ' ')}</p>
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-dark-700">
                          {selectedItem.url ? (
                            <button
                              onClick={() => handleExternalLink(selectedItem.url!)}
                              className="w-full bg-gradient-to-r from-primary-400 to-primary-500 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              <ExternalLink className="w-5 h-5" />
                              View Source
                            </button>
                          ) : (
                            <div className="text-center text-gray-400 py-3">
                              <p className="text-sm">Real-time data from DisasterConnect monitoring system</p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      // Render NewsArticle details
                      <>
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-400">{selectedItem.source_name || 'Unknown Source'}</span>
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-400">
                                {new Date(selectedItem.pubDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <h1 className="text-2xl font-bold text-white mb-3">
                            {selectedItem.title}
                          </h1>
                          {selectedItem.creator && selectedItem.creator.length > 0 && (
                            <p className="text-sm text-gray-400 mb-3">
                              By {selectedItem.creator.join(', ')}
                            </p>
                          )}
                        </div>

                        {selectedItem.image_url && (
                          <div className="mb-6">
                            <img
                              src={selectedItem.image_url}
                              alt={selectedItem.title}
                              className="w-full h-48 object-cover rounded-lg"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        <div className="prose prose-invert max-w-none">
                          <p className="text-lg text-gray-300 mb-4 font-medium">
                            {selectedItem.description}
                          </p>
                          
                          {selectedItem.content && selectedItem.content !== selectedItem.description && (
                            <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                              {selectedItem.content}
                            </div>
                          )}
                        </div>

                        {selectedItem.keywords && selectedItem.keywords.length > 0 && (
                          <div className="mt-6">
                            <h4 className="text-sm font-semibold text-gray-400 mb-2">Keywords</h4>
                            <div className="flex flex-wrap gap-2">
                              {selectedItem.keywords.slice(0, 8).map((keyword, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-dark-700 text-gray-300 rounded text-xs"
                                >
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-6 pt-4 border-t border-dark-700">
                          <button
                            onClick={() => handleExternalLink(selectedItem.link)}
                            className="w-full bg-gradient-to-r from-primary-400 to-primary-500 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                          >
                            <ExternalLink className="w-5 h-5" />
                            Read Full Article
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-center p-6">
                    <div>
                      <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {initialDisaster ? 'Disaster Information' : 'Select an Article'}
                      </h3>
                      <p className="text-gray-400">
                        {initialDisaster 
                          ? 'Detailed information about this disaster event'
                          : userLocation
                          ? `Choose an article to read local news for ${getLocationText()}`
                          : 'Choose an article from the list to read the full content'
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DisasterNewsModal;